import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { AlertTriangle, TrendingDown } from 'lucide-react';

/**
 * "Weakest topic this week" nudge card.
 *
 * Silently hides itself if:
 *   - the API call fails, or
 *   - the student has < 4 attempts in every topic in the last 7 days, or
 *   - every topic is already ≥ 85 % accurate.
 */
const WeaknessNudgeCard = () => {
  const navigate = useNavigate();
  const [nudge, setNudge] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const backend = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${backend}/api/progress/weakness-nudge`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (alive && data.has_nudge) setNudge(data);
      } catch (e) {
        // silent — card just stays hidden
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (loading || !nudge) return null;

  const { display_topic, label, subtopic, accuracy, attempts, practice_route, window_days } = nudge;

  return (
    <Card
      className="p-5 rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 via-orange-50 to-white mb-6"
      data-testid="weakness-nudge-card"
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
            <TrendingDown className="w-6 h-6" strokeWidth={2.5} />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="font-heading font-bold text-slate-900" data-testid="weakness-nudge-title">
                Weakest topic this week
              </h3>
              <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5 rounded-full text-[11px] font-bold">
                <AlertTriangle className="w-3 h-3" />
                {accuracy}% correct
              </span>
            </div>
            <p className="text-sm text-slate-700" data-testid="weakness-nudge-topic">
              You've been finding <strong>{display_topic}</strong>
              {subtopic ? <> in <strong>{label}</strong></> : null} tricky —{' '}
              only {accuracy}% correct across your last {attempts} attempts (past {window_days} days).
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Prof.Scaly recommends a quick focused revision session to lock this in.
            </p>
          </div>
        </div>
        <Button
          className="bg-amber-600 hover:bg-amber-700 text-white font-bold"
          onClick={() => navigate(practice_route)}
          data-testid="weakness-nudge-cta"
        >
          Practice now
        </Button>
      </div>
    </Card>
  );
};

export default WeaknessNudgeCard;
